import React, { useState, useEffect } from 'react';
import { fetchProfile, updateProfile, fetchSexes as fetchSexesAPI, changePassword } from '../utils/api';
import { getDateFormat, setDateFormat } from '../utils/date';
import { mapErrorMessage } from '../utils/errorMapping';

const Profile = () => {
    const [profile, setProfile] = useState(null);
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');
    const [editMode, setEditMode] = useState(false);
    const [editableProfile, setEditableProfile] = useState(null);
    const [sexes, setSexes] = useState([]);
    const [dateFormat, setDateFormatState] = useState(getDateFormat());
    const [passwordData, setPasswordData] = useState({
        old_password: '',
        new_password: '',
        confirm_password: ''
    });
    const [changePasswordMode, setChangePasswordMode] = useState(false);

     useEffect(() => {
        const loadData = async () => {
            try {
                const profileData = await fetchProfile();
                setProfile(profileData.data);
                setEditableProfile(profileData.data);

                const sexesData = await fetchSexesAPI();
                const sexesArray = Object.keys(sexesData.data).map(key => ({
                    id: parseInt(key, 10),
                    name: sexesData.data[key]
                }));
                setSexes(sexesArray);
            } catch (error) {
                setError(mapErrorMessage(error.message));
            }
        };

        loadData();
    }, []);

    useEffect(() => {
        const event = new CustomEvent('dateFormatChanged');
        window.dispatchEvent(event);
    }, [dateFormat]);

    useEffect(() => {
        const event = new CustomEvent('dateFormatChanged');
        window.dispatchEvent(event);
    }, [dateFormat]);

    const handleEditToggle = () => {
        setEditMode(!editMode);
        if (editMode) {
            setEditableProfile(profile);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        if (name === 'sex') {
            const sexId = parseInt(value, 10);
            const selectedSex = sexes.find(s => s.id === sexId);
            setEditableProfile({
                ...editableProfile,
                sex: selectedSex || editableProfile.sex
            });
            return;
        }

        setEditableProfile({ ...editableProfile, [name]: value });
    };

    const handleSave = async () => {
        if (!editableProfile.first_name || !editableProfile.last_name) {
            setError("First and last names cannot be empty.");
            return;
        }
        setError('');
        setMessage('');

        try {
            const updatedProfile = await updateProfile({
                first_name: editableProfile.first_name,
                middle_name: editableProfile.middle_name,
                last_name: editableProfile.last_name,
                sex: editableProfile.sex.id,
                description: editableProfile.description,
                phone: editableProfile.phone
            });
            setDateFormat(dateFormat);
            setProfile(updatedProfile.data);
            setEditableProfile(updatedProfile.data);
            setEditMode(false);
            setMessage('Profile updated successfully!');
        } catch (error) {
            setError(mapErrorMessage(error.message));
        }
    };

    const handlePasswordInputChange = (e) => {
        const { name, value } = e.target;
        setPasswordData({ ...passwordData, [name]: value });
    };

    const handleChangePassword = async () => {
        if (passwordData.new_password !== passwordData.confirm_password) {
            setError("New passwords do not match.");
            return;
        }
        setError('');
        setMessage('');

        try {
            await changePassword(passwordData.old_password, passwordData.new_password, passwordData.confirm_password);
            setChangePasswordMode(false);
            setPasswordData({
                old_password: '',
                new_password: '',
                confirm_password: ''
            });
            setMessage('Password changed successfully!');
        } catch (error) {
            setError(mapErrorMessage(error.message));
        }
    };
 
     return (
        <div>
            <div className="header">
                <h1>Profile</h1>
                <button onClick={handleEditToggle} className="submit-button text-button">
                    {editMode ? 'Cancel' : 'Edit'}
                </button>
            </div>
            <div className="content-area">
                {error && <p className="error">{error}</p>}
                {message && <p className="message">{message}</p>}
                {!profile && !error && <p>Loading profile...</p>}
                {profile && (
                    <div className="profile-details">
                        {editMode ? (
                            <>
                                <div className="form-group">
                                    <input id="first_name" type="text" name="first_name" value={editableProfile.first_name} onChange={handleInputChange} placeholder='First Name'/>
                                </div>
                                <div className="form-group">
                                    <input id="middle_name" type="text" name="middle_name" value={editableProfile.middle_name || ''} onChange={handleInputChange} placeholder='Middle Name'/>
                                </div>
                                <div className="form-group">
                                    <input id="last_name" type="text" name="last_name" value={editableProfile.last_name} onChange={handleInputChange} placeholder='Last Name'/>
                                </div>
                                <div className="form-group">
                                    <input id="phone" type="text" name="phone" value={editableProfile.phone || ''} onChange={handleInputChange} placeholder='Phone'/>
                                </div>
                                <div className="form-group">
                                    <textarea id="description" name="description" value={editableProfile.description || ''} onChange={handleInputChange}placeholder='Description'></textarea>
                                </div>
                                <div className="form-group">
                                    <div className="select-container">
                                        <select id="sex" name="sex" value={editableProfile.sex ? editableProfile.sex.id : ''} onChange={handleInputChange}>
                                            <option>Select Sex</option>
                                            {sexes.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                                        </select>
                                    </div>
                                </div>
                               <div className="form-group">
                                   <label htmlFor="dateFormat">Date Format</label>
                                   <div className="select-container">
                                       <select id="dateFormat" name="dateFormat" value={dateFormat} onChange={(e) => setDateFormatState(e.target.value)}>
                                           <option value="mm/dd/yyyy">mm/dd/yyyy</option>
                                           <option value="month dd, yyyy">Month dd, yyyy</option>
                                       </select>
                                   </div>
                               </div>
                                <button onClick={handleSave} className="submit-button text-button">Save</button>
                            </>
                        ) : (
                            <>
                                <p><strong>ID:</strong> {profile.id}</p>
                                <p><strong>First Name:</strong> {profile.first_name}</p>
                                {profile.middle_name && <p><strong>Middle Name:</strong> {profile.middle_name}</p>}
                                <p><strong>Last Name:</strong> {profile.last_name}</p>
                                <p><strong>Email:</strong> {profile.email}</p>
                                {profile.phone && <p><strong>Phone:</strong> {profile.phone}</p>}
                                {profile.description && <p><strong>Description:</strong> {profile.description}</p>}
                                <p><strong>Sex:</strong> {profile.sex.name}</p>
                            </>
                        )}
                        <div className="password-section">
                            {!changePasswordMode && !editMode && (
                                <button onClick={() => setChangePasswordMode(true)} className="cancel-button text-button">
                                    Change Password
                                </button>
                            )}
                            {changePasswordMode && !editMode && (
                                <>
                                    <div className="form-group">
                                        <input id="old_password" type="password" name="old_password" value={passwordData.old_password} onChange={handlePasswordInputChange} placeholder='Old Password'/>
                                    </div>
                                    <div className="form-group">
                                        <input id="new_password" type="password" name="new_password" value={passwordData.new_password} onChange={handlePasswordInputChange} placeholder='New Password'/>
                                    </div>
                                    <div className="form-group">
                                        <input id="confirm_password" type="password" name="confirm_password" value={passwordData.confirm_password} onChange={handlePasswordInputChange} placeholder='Confirm New Password' />
                                    </div>
                                    <button onClick={() => setChangePasswordMode(false)} className="cancel-button text-button margin-right-button">Cancel</button>
                                    <button onClick={handleChangePassword} className="submit-button text-button">Update Password</button>
                                </>
                            )}
                        </div>
                     </div>
                 )}
             </div>
         </div>
    );
};

export default Profile;